import {AttrsSchema, Component} from 'panel';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import type {Point, Series} from 'highcharts';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {
  FlatSegment,
  InsightsBookmarkParams,
  InsightsDeprecatedLegendUrlData,
  InsightsQueryResults,
} from 'common/report/insights/util/types';
import {ProjectTimezone} from 'common/report/stores/project-details';
import {
  getDisplayOptions,
  getSortConfig,
  getSortedIsoDateKeysFromTimeseries,
  insightsBookmarkParamsToChartParams,
  lookupSegment,
  memoizedGetTimestamp,
} from 'common/report/insights/util';
import {ChartsContextRegistry} from 'common/charts/contexts/registry';
import {SegmentFilterController} from 'common/charts/contexts/segment-filter-controller';
import {
  ClauseType,
  computeMetricClauseByKey,
  LineChartTooltipStat,
  RenderLineChartTooltipParams,
  ShowClauseData,
} from 'common/report/insights/util/tooltip';
import {formatDate} from 'common/util/date';
import {PopoverCallback} from 'common/charts/types';
import {CurrentProject, CurrentProjectResource} from 'common/report/model-resources/projects';
import {dataGroupStore} from 'common/report/stores/data-groups';
import {getWorkspaceId} from 'common/report/util';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {ChartType, REPORT_LIMITS, ReportName, RESULTS_OVERALL_SUM_KEY} from 'common/report/constants';
import {LiftType} from 'common/report/util/lift/types';
import {OTHER_SEGMENT_VALUE} from 'common/report/insights/util/constants';
import {formatMetricNumber} from 'common/util';
import {ForecastValue, ParamsAndResults} from 'common/report/types';
import {deselectPoint, selectPoint} from 'common/report/util/highcharts';
import {SortConfig} from 'common/report/util/sorting/types';

import {getLineChartData, LineChartData, OtherSegmentData} from './util';
import {template} from './template';
import css from './index.inline.styl';

interface InsightsLineChartState {
  lineChartData: null | LineChartData;
  /** clicked highcharts point object to display a popover for */
  popoverSelection: Point | null;
  /** holds positioning of the popover for the active highcharts point */
  popoverStyles: {
    visibility?: string;
    left?: string;
    top?: string;
  } | null;
}

export interface InsightsLineChartJsxProps {
  attrs: {
    loaded: boolean;
    failed?: boolean;
    'show-annotations'?: boolean;
    'utc-offset'?: number;
    timezone?: string;
  };
  props: {
    // use a single prop for bookmark params and results so they can be updated together atomically
    paramsAndResults: ParamsAndResults<InsightsBookmarkParams, InsightsQueryResults> | null;

    // legend data will eventually be persisted in bookmark, URL persistence is a stopgap
    urlLegendData?: InsightsDeprecatedLegendUrlData;
    popoverCalback?: PopoverCallback;
    dataGroups?: Record<string, DataGroup>;
  };
}

export class InsightsLineChart extends Component<
  InsightsLineChartState,
  InsightsLineChartJsxProps['attrs'],
  any,
  any,
  ChartsContextRegistry
> {
  static get attrsSchema(): AttrsSchema<InsightsLineChartJsxProps['attrs']> {
    return {
      ...super.attrsSchema,
      loaded: `boolean`,
      failed: `boolean`,
      'show-annotations': `boolean`,
      'utc-offset': `number`,
      timezone: `string`,
    };
  }

  protected project: CurrentProject | null = null;
  protected projectTimezone: ProjectTimezone | null = null;
  // optional dataGroups props in case dataGroupStore is not available (e.g. Public Dashboards)
  private _dataGroups: Record<string, DataGroup> | null = null;

  private _bookmarkParams: InsightsBookmarkParams | null = null;
  private _results: InsightsQueryResults | null = null;

  public get results(): InsightsQueryResults | null {
    return this._results;
  }

  public get bookmarkParams(): InsightsBookmarkParams | null {
    return this._bookmarkParams;
  }

  public set paramsAndResults({
    params,
    results,
  }: ParamsAndResults<InsightsBookmarkParams, InsightsQueryResults> | null) {
    if (!isEqual(this.bookmarkParams, params) || !isEqual(this.results, results)) {
      this._bookmarkParams = params;
      this._results = results;
      this.updateLineChartData();
      this.updatePersistedLegendSelections();
    }
  }

  private _urlLegendData: InsightsDeprecatedLegendUrlData | null = null;
  public get urlLegendData(): InsightsDeprecatedLegendUrlData | null {
    return this._urlLegendData;
  }
  public set urlLegendData(value: InsightsDeprecatedLegendUrlData | null) {
    if (!isEmpty(value)) {
      this._urlLegendData = value;
      this.updatePersistedLegendSelections();
    }
  }

  protected get segmentFilterController(): SegmentFilterController {
    return this.getContext(`segmentFilterController`);
  }

  public popoverCallback: PopoverCallback | null = null;

  get config() {
    return {
      useShadowDom: true,
      css,
      template,
      defaultState: {
        lineChartData: null,
        popoverStyles: null,
        popoverSelection: null,
      },
      contexts: [`segmentFilterController` as const, `currentProject` as const],
      defaultContexts: {
        segmentFilterController: new SegmentFilterController(),
      },
    };
  }

  connectedCallback() {
    super.connectedCallback();
    this.updateTimezoneFromContext();
    this.updateLineChartData();
    this.updatePersistedLegendSelections();
  }

  getCurrentProject(): CurrentProject {
    return this.getContext(`currentProject`).get().value;
  }

  updateTimezoneFromContext() {
    // check public dashboard
    if (!this._dataGroups) {
      const currentProject = this.getCurrentProject();
      if (currentProject) {
        this.projectTimezone = {
          utcOffset: currentProject.utcOffset,
          timezone: currentProject.timezone,
        };
      }
    }
  }

  resourceCustomSubscriptions = new Map([
    [
      CurrentProjectResource,
      () => {
        // this component disables project & dataGroup Stores if equivalent attributes are specified, this may be required
        // in environments where Stores don't work, eg. Public Dashboards
        if (!this._dataGroups && !this.hasProjectTimeAttributes()) {
          const currentProject = this.getCurrentProject();
          if (this.project?.id !== currentProject.id) {
            const projectId = currentProject.id;
            const workspaceId = parseInt(getWorkspaceId(), 10);
            // Note: data groups are only used in tooltips and (as of writing) do not need to trigger state updates/renders or annotated with people properties
            dataGroupStore.fetch({projectId, workspaceId}); // async, no need to wait
          }
          if (!isEqual(this.project, currentProject)) {
            this.project = currentProject;
            this.updateTimezoneFromContext();
            this.updateLineChartData();
          }
        }
      },
    ],
  ]);

  protected hasProjectTimeAttributes(): boolean {
    return Boolean(this.attr(`timezone`).length);
  }

  protected hasRequiredParams(): boolean {
    // Props may have been set before connectedCallback() fires, therefore need to ensure
    // this component is connected before accessing a context (e.g. segmentFilterController)
    return !!(this.isConnected && this.getProjectTimezone() && this.bookmarkParams && this.results);
  }

  protected getProjectTimezone(): ProjectTimezone | null {
    if (this.hasProjectTimeAttributes()) {
      return {
        utcOffset: this.attr(`utc-offset`),
        timezone: this.attr(`timezone`),
      };
    }
    return this.projectTimezone;
  }

  private updateLineChartData(): void {
    if (this.hasRequiredParams()) {
      const lineChartData = getLineChartData(
        this.getProjectTimezone(),
        // extracting only the actual values used allows for better memoization
        insightsBookmarkParamsToChartParams(this.bookmarkParams),
        this.results,
        this.getDataGroups(),
      );
      this.update({lineChartData});

      const {meta} = this.results;
      const {apiResultsTruncated, queryLimits} = meta;

      this.segmentFilterController.loadSections(
        [
          {
            items: lineChartData.legendSegmentItems,
            metadata: lineChartData.legendIdToIndexMap, // Insights app is expected to use this mapping to persist legend selection indices to the URL
            apiResultsTruncated,
            tableColumns: lineChartData.tableColumns,
            readOnlyItems: lineChartData.readOnlyLegendItems,
          },
        ],
        {
          defaultNumSelections: REPORT_LIMITS[ReportName.Insights].segments[ChartType.Line].default,
          maxNumSelections: REPORT_LIMITS[ReportName.Insights].segments[ChartType.Line].max,
          tableSortConfig: getSortConfig({
            bookmarkSortConfig: this.bookmarkParams.sorting,
            headerCount: this.results.headers.length,
          })[ChartType.Line] as SortConfig,
        },
      );

      this.segmentFilterController.segmentationQueryLimit = queryLimits?.limit;
      this.segmentFilterController.exportSegmentationQueryLimit = REPORT_LIMITS[ReportName.Insights].csvExport;
    } else {
      this.update({lineChartData: null});
    }
  }

  private updatePersistedLegendSelections() {
    const individualSelectedSegments = this.bookmarkParams?.legend?.individualSelectedSegments;
    const indexSelections = this.urlLegendData?.idxSelections;

    if (this.isConnected && (individualSelectedSegments || !indexSelections)) {
      this.segmentFilterController.loadFromSavedLegend(
        individualSelectedSegments ? [individualSelectedSegments] : null,
      );
      return;
    }

    const {lineChartData} = this.state;
    if (
      this.state.lineChartData &&
      Array.isArray(indexSelections) &&
      indexSelections.length > 0 &&
      !Array.isArray(indexSelections[0])
    ) {
      const selectedIds = (indexSelections as number[]).map(
        (legendIndex) => lineChartData.legendIndexToId[legendIndex],
      );

      this.segmentFilterController.loadPersistedSelections([selectedIds], {shouldInvokeToggleCallbacks: true});
    }
  }

  protected generatePointTooltipParams(
    highchartsContext: {
      point: Point;
      series: Series;
      percentage: number;
    },
    otherSegmentData?: OtherSegmentData,
  ): RenderLineChartTooltipParams {
    const {point, series, percentage} = highchartsContext;
    const segmentKey = series.options.name; // use name from options because highcharts randomly names series with an empty string name
    const showPreviousValueDelta = !Number.isFinite(percentage) && point.index > 0;
    const {segmentLookup} = this.state.lineChartData;
    const metricClauseByKey = computeMetricClauseByKey({
      sectionsMetadata: this.results.meta.report_sections,
      bookmarkParams: this.bookmarkParams,
      dataGroups: Object.values(this.getDataGroups()),
    });

    let mainSegment: FlatSegment = segmentLookup.get(segmentKey);
    let metricClauseData = metricClauseByKey[mainSegment?.path?.[0]];
    if (segmentKey === OTHER_SEGMENT_VALUE) {
      mainSegment = otherSegmentData.segment;
      metricClauseData = {
        clauseType: ClauseType.Other,
        metricClauses: otherSegmentData.metricKeys.map((key) => metricClauseByKey[key]),
        metricKey: OTHER_SEGMENT_VALUE,
      };
    }

    const mainDates = getSortedIsoDateKeysFromTimeseries(mainSegment.value);
    const deltaStat: LineChartTooltipStat = showPreviousValueDelta
      ? {
          value: mainSegment.value[mainDates[point.index - 1]],
        }
      : null;

    const stat: LineChartTooltipStat = {
      ...this.formatTooltipStat(point, mainSegment),
      dateRange: {
        fromDate: this.results.date_range.from_date,
        toDate: this.results.date_range.to_date,
      },
      segmentKey: `arbitrary`,
    };

    let timeComparisonStat: null | LineChartTooltipStat = null;
    let liftComparisonStat: null | LineChartTooltipStat = null;
    let baselineComparisonStat: null | LineChartTooltipStat = null;
    let overallComparisonStat: null | LineChartTooltipStat = null;
    let forecastComparisonStat: null | LineChartTooltipStat<ForecastValue> = null;

    if (this.state.lineChartData.liftComparisonSegmentLookup.has(segmentKey)) {
      const liftComparisonSegment = lookupSegment(
        segmentKey,
        this.state.lineChartData.liftComparisonSegmentLookup,
        mainSegment.path,
      );

      liftComparisonStat = {
        ...this.formatTooltipStat(point, liftComparisonSegment),
        // we only want to display the leaf segment name
        segmentKey: segmentKey.split(`,`).slice(-1)[0],
      };

      const {type}: {type: LiftType} = this.results.meta.liftComparison;

      switch (type) {
        case LiftType.Time:
          // Time will be calculated with the timeComparisonSegmentLookup
          break;
        case LiftType.Overall:
          overallComparisonStat = {
            ...this.formatTooltipStat(point, lookupSegment(RESULTS_OVERALL_SUM_KEY, segmentLookup, mainSegment.path)),
            segmentKey: `Overall`,
          };
          break;
        case LiftType.Segment: {
          const baselineSegmentKey = [
            ...segmentKey.split(`,`).slice(0, -1),
            this.results.meta.liftComparison.groupValue || this.results.meta.liftComparison.cohort.name,
          ].join(`,`);
          if (baselineSegmentKey) {
            baselineComparisonStat = {
              ...this.formatTooltipStat(point, lookupSegment(baselineSegmentKey, segmentLookup, mainSegment.path)),
              segmentKey: baselineSegmentKey,
            };
          }
          break;
        }
      }
    }

    if (this.state.lineChartData.timeComparisonSegmentLookup.has(segmentKey)) {
      const comparisonSegment = lookupSegment(segmentKey, this.state.lineChartData.timeComparisonSegmentLookup);
      timeComparisonStat = {
        ...this.formatTooltipStat(point, comparisonSegment),
        dateRange: {
          fromDate: this.results.time_comparison.date_range.from_date,
          toDate: this.results.time_comparison.date_range.to_date,
        },
      };
    }

    if (this.state.lineChartData.forecastComparisonSegmentLookup.has(segmentKey)) {
      const forecastSegment = lookupSegment<ForecastValue>(
        segmentKey,
        this.state.lineChartData.forecastComparisonSegmentLookup,
      );
      forecastComparisonStat = this.formatTooltipForecastValueStat(point, forecastSegment);
      // for charts with forecast comparisons, use the point values for the stat since
      // anomaly marker points don't map 1 to 1 on the main series
      stat.value = point.y;
      stat.timestamp = point.x;
    }

    const displayOptions = getDisplayOptions(this.bookmarkParams.sections, this.bookmarkParams.displayOptions);
    const isFirstInterval = point.index === 0;
    const isLastInterval = point.index === series.data.length - 1;

    return {
      headers: this.results.headers,
      metricClauseData,
      // this populates the breakdown subheading, exclude the primary metric in the segment array
      segments: mainSegment.path.slice(1),
      stat,
      deltaStat,
      timeComparisonStat,
      liftComparisonStat,
      baselineComparisonStat,
      overallComparisonStat,
      forecastComparisonStat,
      percent: percentage,
      timeUnit: displayOptions.timeUnit,
      isFirstInterval,
      isLastInterval,
      titleColor: series.options.color as string,
      utcOffset: this.getProjectTimezone().utcOffset,
      // @ts-ignore Property 'isComparisonItem' does not exist on type 'SeriesOptionsType'.
      isComparisonItem: series.userOptions.isComparisonItem,
    };
  }

  private getDataGroups(): Record<string, DataGroup> {
    if (this._dataGroups) {
      return this._dataGroups;
    } else {
      return dataGroupStore.value ?? {};
    }
  }

  hasForecastComparison() {
    return this.state.lineChartData.forecastComparisonSegmentLookup?.size > 0;
  }

  formatTooltipStat(point, segment: FlatSegment): LineChartTooltipStat {
    const stat = this.formatTooltipStatLogic(point, segment);
    stat.value = stat.value ?? 0;
    return stat;
  }

  formatTooltipForecastValueStat(point, segment: FlatSegment<ForecastValue>): LineChartTooltipStat<ForecastValue> {
    // for charts with forecast comparisons, find the true index for the point first since
    // anomaly marker points don't map 1 to 1 on the main series
    const forecastPointIndex = this.state.lineChartData.lineChartSeries[0].data.findIndex(
      (dataItem) => dataItem[0] === point.x,
    );
    const forecastPoint = {...point, index: forecastPointIndex};
    const stat = this.formatTooltipStatLogic<ForecastValue>(forecastPoint, segment);
    stat.value = stat.value ?? {yhat_lower: 0, yhat_upper: 0};
    return stat;
  }

  formatTooltipStatLogic<ValueT = number>(point, segment: FlatSegment<ValueT>): LineChartTooltipStat<ValueT> {
    const dates = getSortedIsoDateKeysFromTimeseries(segment.value);
    const value: ValueT = segment.value[dates[point.index]];

    const timestamp = memoizedGetTimestamp(
      dates[point.index],
      this.getProjectTimezone().timezone,
      this.getProjectTimezone().utcOffset,
    );

    return {
      value,
      timestamp,
    };
  }

  formatYAxisLabel(y: number): string {
    return formatMetricNumber(y) + (this.results?.lift_comparison ? `%` : ``);
  }

  getPointDataFromSelection() {
    if (this.state.popoverSelection === null) {
      return null;
    }

    const point = this.state.popoverSelection;
    return this.generatePointTooltipParams({point, series: point.series, percentage: point.percentage});
  }

  // Arrow syntax is required to bind "this", which is otherwise changed by highcharts
  handlePointClick = (point: Point) => {
    const {series, plotX, plotY} = point;
    if (series.options.name === OTHER_SEGMENT_VALUE) {
      return;
    }

    const pointData = this.generatePointTooltipParams({point, series, percentage: point.percentage});
    const metricKey = (pointData.metricClauseData as ShowClauseData).metricKey;
    const hasPopoverActions = this.popoverCallback?.call(null, metricKey, pointData.segments, {
      highchartsPointContext: point,
    }).length;

    if (hasPopoverActions) {
      selectPoint(point);

      const left = series.chart.plotLeft + plotX + 12;
      const top = series.chart.plotTop + plotY + 12;
      this.update({
        popoverStyles: {left: `${left}px`, top: `${top}px`},
        popoverSelection: point,
      });
    }
  };

  getPopoverAttrs() {
    const pointData = this.getPointDataFromSelection();
    const metricKey = (pointData.metricClauseData as ShowClauseData).metricKey;

    return {
      open: true,
      title: metricKey,
      subtitle: formatDate(pointData.stat.timestamp, {unit: pointData.timeUnit}),
      'button-options': this.popoverCallback(metricKey, pointData.segments, {
        highchartsPointContext: this.state.popoverSelection,
      }),
    };
  }

  handlePopoverAction(ev: CustomEvent) {
    this.dispatchEvent(
      new CustomEvent(`popoverAction`, {
        detail: {...ev.detail, pointData: this.getPointDataFromSelection()},
      }),
    );
    this.handlePopoverClose();
  }

  handlePopoverClose() {
    const selectedPoint = this.state.popoverSelection;
    if (selectedPoint) {
      deselectPoint(selectedPoint);
    }
    this.update({
      popoverSelection: null,
      popoverStyles: null,
    });
  }

  public get dataGroups(): Record<string, DataGroup> | null {
    return this._dataGroups;
  }

  public set dataGroups(value: Record<string, DataGroup> | null) {
    this._dataGroups = value;
    this.updateLineChartData();
  }
}

export const InsightsLineChartEl = defineAndMakeJsxFactory<InsightsLineChartJsxProps>(
  `mp-insights-line-chart`,
  InsightsLineChart,
);
export const InsightsLineChartReact = panel2react<InsightsLineChartJsxProps>(
  `mp-insights-line-chart`,
  InsightsLineChart,
);
